import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ChevronRight,
  Flag,
  CircleDot,
  Target,
  TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart as LineChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemberPortalApi } from "../api/memberPortalApiClient";
import { useGolfStatsText } from "./GolfPersonalStats";
import {
  membersNavigationState,
  useLetsPlayBack,
} from "../lib/letsPlayNavigation";
const metrics = [
  ["putting", "putting", "putt_count", "lower"],
  ["fairways", "fairways", "fairway_count", "higher"],
  ["greens", "greens", "gir_count", "higher"],
];
export default function GolfProgress({ clubCode, onBack }) {
  const { t, locale } = useGolfStatsText(),
    { memberPortalApiGet } = useMemberPortalApi(),
    navigate = useNavigate(),
    location = useLocation();
  const back = useLetsPlayBack(clubCode, `/${clubCode}/lets-play/golf`);
  const [filters, setFilters] = useState(() => {
    try {
      return (
        JSON.parse(sessionStorage.getItem(`golf-progress:${clubCode}`)) || {
          holes: 18,
          period: 10,
          page: 0,
          tab: "overview",
        }
      );
    } catch {
      return { holes: 18, period: 10, page: 0, tab: "overview" };
    }
  });
  const metric = ["putting", "fairways", "greens"].includes(filters.metric)
    ? filters.metric
    : "putting";
  const [data, setData] = useState(null),
    [error, setError] = useState(false),
    [loading, setLoading] = useState(true),
    [request, setRequest] = useState(0);
  const update = (patch) =>
    setFilters((v) => ({ ...v, ...patch, page: patch.page ?? 0 }));
  useEffect(() => {
    try {
      sessionStorage.setItem(
        `golf-progress:${clubCode}`,
        JSON.stringify(filters),
      );
    } catch {}
  }, [filters, clubCode]);
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    const params = new URLSearchParams({
      holes: filters.holes,
      period: filters.period,
      page: filters.page || 0,
    });
    if (filters.scope) {
      const [courseId, teeId] = filters.scope.split(":");
      params.set("courseId", courseId);
      params.set("teeId", teeId);
    }
    memberPortalApiGet(
      `/api/members-portal/${clubCode}/lets-play/golf/progress?${params}`,
    )
      .then(async (r) => {
        if (!r.ok) throw Error();
        const p = await r.json();
        if (live) {
          if (
            p.data.options.length &&
            !filters.scope &&
            !p.data.options.some(
              (o) =>
                `${o.course_id}:${o.tee_id}` === filters.scope &&
                o.holes === filters.holes,
            )
          ) {
            const option = p.data.options[0];
            setFilters((v) => ({
              ...v,
              scope: `${option.course_id}:${option.tee_id}`,
              holes: option.holes,
              page: 0,
            }));
            return;
          }
          setData(p.data);
        }
      })
      .catch(() => live && setError(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [
    clubCode,
    filters.scope,
    filters.holes,
    filters.period,
    filters.page,
    request,
    memberPortalApiGet,
  ]);
  const format = (value) =>
    value == null
      ? "—"
      : new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
          value,
        );
  const scoreFormat = (value) =>
    value == null
      ? "—"
      : value === 0
        ? "E"
        : new Intl.NumberFormat(locale, {
            maximumFractionDigits: 1,
            signDisplay: "exceptZero",
          }).format(value);
  const date = (value) =>
    new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeZone: data?.timezone || "UTC",
    }).format(new Date(value));
  const options = Array.from(
    new Map(
      (data?.options || []).map((o) => [`${o.course_id}:${o.tee_id}`, o]),
    ).entries(),
  );
  const chart = (key, title, count, unit = "") => {
    const chronological = [...(data?.rounds || [])].reverse(),
      usable = chronological.filter((r) => r[key] != null);
    return (
      <>
        <div
          className={
            usable.length > 1 ? "mp-progress-chart" : "mp-progress-chart-sparse"
          }
          role="img"
          aria-label={`${title}. ${t("chartDescription")}`}
        >
          {usable.length > 1 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chronological.map((r) => ({
                  ...r,
                  label: date(r.started_at),
                }))}
                margin={{ left: 0, right: 16, top: 10, bottom: 8 }}
              >
                <CartesianGrid stroke="#e8ece5" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  minTickGap={40}
                />
                <YAxis
                  tickFormatter={
                    key === "score_to_par" ? scoreFormat : undefined
                  }
                  width={38}
                  tick={{ fontSize: 11 }}
                  unit={unit}
                />
                <Tooltip
                  formatter={(value) => [
                    `${key === "score_to_par" ? scoreFormat(value) : format(value)}${unit}`,
                    title,
                  ]}
                />
                <Area
                  type="linear"
                  dataKey={key}
                  stroke="none"
                  fill="#e4eddf"
                  fillOpacity={0.5}
                  connectNulls={false}
                  tooltipType="none"
                  isAnimationActive={false}
                />
                <Line
                  type="linear"
                  dataKey={key}
                  stroke="#183449"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p>
              {usable.length === 1
                ? `${key === "score_to_par" ? scoreFormat(usable[0][key]) : format(usable[0][key])}${unit} · ${date(usable[0].started_at)}`
                : t("noMetric")}
            </p>
          )}
        </div>
        {usable.length === 1 && <p>{t("oneRound")}</p>}
        {usable.length > 0 && (
          <details>
            <summary>{t("viewData")}</summary>
            {key === "score_to_par" && (
              <p>
                {t("scoreCoverage", {
                  count: data.coverage,
                  total: data.rounds.length,
                })}
              </p>
            )}
            <table>
              <caption>{title}</caption>
              <thead>
                <tr>
                  <th>{t("date")}</th>
                  <th>{t("value")}</th>
                  <th>{t("recorded")}</th>
                </tr>
              </thead>
              <tbody>
                {chronological.map((r) => (
                  <tr key={r.id}>
                    <td>{date(r.started_at)}</td>
                    <td>
                      {key === "score_to_par"
                        ? scoreFormat(r[key])
                        : format(r[key])}
                      {r[key] != null ? unit : ""}
                    </td>
                    <td>
                      {key === "score_to_par"
                        ? r[key] != null
                          ? r.holes
                          : 0
                        : r[count]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </>
    );
  };
  const roundRows = (rounds, metricKey, countKey) => (
    <div>
      {rounds.map((r) => (
        <button
          type="button"
          className="mp-progress-round"
          key={r.id}
          onClick={() =>
            navigate(`/${clubCode}/lets-play/golf/round/${r.id}`, {
              state: membersNavigationState(location),
            })
          }
        >
          <span>
            <strong>{date(r.started_at)}</strong>
            <small>
              {r.course_name} · {r.tee_name} ·{" "}
              {t("holeCount", { count: r.holes })}
            </small>
            <small>
              {metricKey
                ? t("holesRecorded", { count: r[countKey] })
                : metrics
                    .filter(([key]) => r[key] != null)
                    .map(
                      ([key, title, count]) =>
                        `${t(title)} ${format(r[key])}${key !== "putting" ? "%" : ""} · ${t("holesRecorded", { count: r[count] })}`,
                    )
                    .join(" · ")}
            </small>
          </span>
          <span>
            <strong>
              {metricKey
                ? `${format(r[metricKey])}${metricKey !== "putting" ? "%" : ""}`
                : r.score_count
                  ? r.gross
                  : "—"}
            </strong>
            <small>
              {!metricKey &&
                (r.score_to_par != null
                  ? scoreFormat(r.score_to_par)
                  : t("scoredCoverage", {
                      count: r.score_count,
                      total: r.holes,
                    }))}
            </small>
            <ChevronRight size={16} />
          </span>
        </button>
      ))}
    </div>
  );
  return (
    <section className="mp-progress">
      <button type="button" onClick={onBack || back}>
        <ArrowLeft size={18} />
        {t("back")}
      </button>
      <h1>{t("progress")}</h1>
      <p>{t("subtitle")}</p>
      {(loading || error || data?.options.length > 0) && (
        <div className="mp-progress-filters">
          <label>
            <span className="mp-golf-filter-label">{t("courseTee")}</span>
            <select
              aria-label={t("courseTee")}
              value={filters.scope || ""}
              disabled={!options.length}
              onChange={(e) => {
                const option = options.find(
                  ([key]) => key === e.target.value,
                )?.[1];
                update({ scope: e.target.value, holes: option?.holes || 18 });
              }}
            >
              {!options.length && <option value="">{t("noHistory")}</option>}
              {options.map(([key, o]) => (
                <option key={key} value={key}>
                  {o.course_name} · {o.tee_name}
                </option>
              ))}
            </select>
          </label>
          <div className="mp-progress-filter-row">
            <fieldset className="mp-progress-hole-choice">
              <legend className="mp-golf-filter-label">{t("holes")}</legend>
              <div
                className="mp-progress-hole-options"
                role="group"
                aria-label={t("holes")}
              >
                {[9, 18].map((count) => (
                  <button
                    type="button"
                    key={count}
                    aria-pressed={filters.holes === count}
                    disabled={
                      !data?.options.some(
                        (o) =>
                          `${o.course_id}:${o.tee_id}` === filters.scope &&
                          o.holes === count,
                      )
                    }
                    onClick={() => update({ holes: count })}
                  >
                    {t("holeCount", { count })}
                  </button>
                ))}
              </div>
            </fieldset>
            <label>
              <span className="mp-golf-filter-label">{t("period")}</span>
              <select
                aria-label={t("period")}
                value={filters.period}
                onChange={(e) => update({ period: Number(e.target.value) })}
              >
                {[5, 10, 20, 0].map((n) => (
                  <option key={n} value={n}>
                    {n ? t("lastRounds", { count: n }) : t("allTime")}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}
      {(loading || error || data?.options.length > 0) && (
        <div
          className="mp-progress-tabs"
          role="tablist"
          aria-label={t("progress")}
        >
          {["overview", "trends", "rounds"].map((tab) => (
            <button
              role="tab"
              id={`golf-progress-tab-${tab}`}
              aria-controls="golf-progress-panel"
              tabIndex={filters.tab === tab ? 0 : -1}
              onKeyDown={(event) => {
                const tabs = ["overview", "trends", "rounds"];
                let index = tabs.indexOf(tab);
                if (event.key === "ArrowRight") index = (index + 1) % 3;
                else if (event.key === "ArrowLeft") index = (index + 2) % 3;
                else if (event.key === "Home") index = 0;
                else if (event.key === "End") index = 2;
                else return;
                event.preventDefault();
                setFilters((v) => ({ ...v, tab: tabs[index] }));
                event.currentTarget.parentElement
                  .querySelectorAll('[role="tab"]')
                  [index]?.focus();
              }}
              aria-selected={filters.tab === tab}
              key={tab}
              onClick={() => setFilters((v) => ({ ...v, tab }))}
            >
              {t(tab)}
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <p role="status">{t("loading")}</p>
      ) : error ? (
        <p role="alert">
          {t("loadError")}{" "}
          <button onClick={() => setRequest((v) => v + 1)}>{t("retry")}</button>
        </p>
      ) : (
        data && (
          <div
            role="tabpanel"
            id="golf-progress-panel"
            aria-labelledby={`golf-progress-tab-${filters.tab}`}
          >
            {!data.options.length ? (
              <div className="mp-progress-empty">
                <Flag size={48} strokeWidth={1.3} aria-hidden="true" />
                <h2>{t("firstTitle")}</h2>
                <p>{t("firstHelp")}</p>
                <button
                  type="button"
                  className="mp-golf-primary"
                  onClick={() =>
                    navigate(`/${clubCode}/lets-play/golf`, {
                      state: membersNavigationState(location),
                    })
                  }
                >
                  {t("startRound")}
                </button>
                <dl>
                  {[
                    ["puttingTab", "puttingHelp"],
                    ["fairwaysTab", "fairwayHelp"],
                    ["greensTab", "girHelp"],
                  ].map(([title, help]) => (
                    <div key={title}>
                      <dt>{t(title)}</dt>
                      <dd>{t(help)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : !data.rounds.length ? (
              <div className="mp-progress-empty">
                <Flag size={32} aria-hidden="true" />
                <h2>{t("noMatch")}</h2>
                <p>{t("noMatchHelp")}</p>
                <button
                  type="button"
                  onClick={() => {
                    const o = data.options[0];
                    update({
                      scope: `${o.course_id}:${o.tee_id}`,
                      holes: o.holes,
                      period: 10,
                      page: 0,
                    });
                  }}
                >
                  {t("resetFilters")}
                </button>
              </div>
            ) : (
              <>
                {filters.period === 0 && (
                  <details>
                    <summary>{t("coverage")}</summary>
                    <p>{t("window")}</p>
                  </details>
                )}
                {filters.tab === "overview" ? (
                  <>
                    {data.coverage > 1 ? (
                      <article className="mp-progress-card">
                        <h2>{t("scoringTrend")}</h2>
                        <dl className="mp-progress-score-summary">
                          <div>
                            <dt>{t("average")}</dt>
                            <dd
                              aria-label={
                                data.summary.averageScore === 0
                                  ? t("evenPar")
                                  : undefined
                              }
                            >
                              {scoreFormat(data.summary.averageScore)}
                            </dd>
                          </div>
                          <div>
                            <dt>{t("best")}</dt>
                            <dd
                              aria-label={
                                data.summary.bestScore === 0
                                  ? t("evenPar")
                                  : undefined
                              }
                            >
                              {scoreFormat(data.summary.bestScore)}
                            </dd>
                          </div>
                        </dl>
                        {chart("score_to_par", t("scoreToPar"))}
                        <small>
                          {t("scoreCoverageShort", {
                            count: data.coverage,
                            total: data.rounds.length,
                          })}
                        </small>
                      </article>
                    ) : data.coverage === 1 ? (
                      <article className="mp-progress-card mp-progress-benchmark">
                        <Flag size={22} aria-hidden="true" />
                        <h2>{t("benchmark")}</h2>
                        <strong
                          className="mp-progress-value"
                          aria-label={
                            data.summary.bestScore === 0
                              ? t("evenPar")
                              : undefined
                          }
                        >
                          {scoreFormat(data.summary.bestScore)}
                        </strong>
                        <small>{t("scoreToPar")}</small>
                        {data.rounds
                          .filter((r) => r.score_to_par != null)
                          .map((r) => (
                            <p key={r.id}>
                              {date(r.started_at)} · {r.course_name} ·{" "}
                              {t("holeCount", { count: r.holes })}
                            </p>
                          ))}
                        <p>{t("benchmarkHelp")}</p>
                        {data.rounds.length > 1 && (
                          <details>
                            <summary>{t("whyOne")}</summary>
                            <p>
                              {t("excludedReasons", {
                                scores: data.rounds.filter(
                                  (r) => r.score_count < r.holes,
                                ).length,
                                par: data.rounds.filter(
                                  (r) =>
                                    r.score_count === r.holes &&
                                    r.score_to_par == null,
                                ).length,
                              })}
                            </p>
                          </details>
                        )}
                      </article>
                    ) : (
                      <article className="mp-progress-card">
                        <h2>{t("noScoring")}</h2>
                        <p>{t("historicalHelp")}</p>
                      </article>
                    )}
                    <div className="mp-progress-section-heading">
                      <h2>{t("roundStats")}</h2>
                      <button
                        onClick={() =>
                          setFilters((v) => ({ ...v, tab: "trends" }))
                        }
                      >
                        {t("viewTrends")}
                        <ChevronRight size={16} />
                      </button>
                    </div>
                    <div className="mp-progress-metrics">
                      {metrics
                        .filter(([key]) => data.summary[key] != null)
                        .map(([key, title, count], index) => {
                          const Icon = {
                            putting: CircleDot,
                            fairways: Flag,
                            greens: Target,
                          }[key];
                          return (
                            <div key={key}>
                              <Icon size={20} aria-hidden="true" />
                              <span>
                                {t(title)}
                                <strong>
                                  {format(data.summary[key])}
                                  {key !== "putting" ? "%" : ""}
                                </strong>
                                <small>
                                  {key === "putting"
                                    ? t("holesRecorded", {
                                        count: data.summary[count],
                                      })
                                    : t("answersRecorded", {
                                        hits: data.summary[
                                          key === "fairways"
                                            ? "fairway_hits"
                                            : "gir_hits"
                                        ],
                                        count: data.summary[count],
                                      })}
                                </small>
                              </span>
                            </div>
                          );
                        })}
                      <div>
                        <TrendingUp size={20} aria-hidden="true" />
                        <span>
                          {t("roundsLogged")}
                          <strong>{format(data.rounds.length)}</strong>
                          <small>
                            {t("loggedCoverage", { count: data.rounds.length })}
                          </small>
                        </span>
                      </div>
                    </div>
                    <details>
                      <summary>{t("coverage")}</summary>
                      <p>
                        {t("recordedOnly")} {t("par45")}
                      </p>
                    </details>
                    <div className="mp-progress-section-heading">
                      <h2>{t("recentRounds")}</h2>
                      <button
                        onClick={() =>
                          setFilters((v) => ({ ...v, tab: "rounds" }))
                        }
                      >
                        {t("viewRounds")}
                        <ChevronRight size={16} />
                      </button>
                    </div>
                    {roundRows(data.rounds.slice(0, 3))}
                  </>
                ) : filters.tab === "trends" ? (
                  <div className="mp-progress-trends">
                    <div
                      className="mp-progress-metric-picker"
                      role="group"
                      aria-label={t("trends")}
                    >
                      {metrics.map(([key]) => (
                        <button
                          type="button"
                          key={key}
                          aria-pressed={metric === key}
                          onClick={() =>
                            setFilters((value) => ({ ...value, metric: key }))
                          }
                        >
                          {t(`${key}Tab`)}
                        </button>
                      ))}
                    </div>
                    {metrics
                      .filter(([key]) => key === metric)
                      .map(([key, title, count, direction]) => (
                        <article className="mp-progress-card" key={key}>
                          <h2>{t(title)}</h2>
                          {data.summary[key] != null && (
                            <>
                              <strong className="mp-progress-value">
                                {format(data.summary[key])}
                                {key !== "putting" ? "%" : ""}
                              </strong>
                              <small>
                                {key === "putting" ? t("perHole") : t(title)}
                              </small>
                              <p>
                                {t(direction)} ·{" "}
                                {t("holesRecorded", {
                                  count: data.summary[count],
                                })}
                              </p>
                            </>
                          )}
                          {chart(
                            key,
                            t(title),
                            count,
                            key === "putting" ? "" : "%",
                          )}
                          {roundRows(
                            data.rounds.filter((r) => r[key] != null),
                            key,
                            count,
                          )}
                        </article>
                      ))}
                  </div>
                ) : (
                  roundRows(data.rounds)
                )}
              </>
            )}
            {filters.period === 0 &&
              data.options.length > 0 &&
              data.rounds.length > 0 && (
                <nav aria-label={t("pagination")}>
                  <button
                    disabled={!filters.page}
                    onClick={() => update({ page: filters.page - 1 })}
                  >
                    {t("previous")}
                  </button>
                  <span> {filters.page + 1} </span>
                  <button
                    disabled={!data.hasMore}
                    onClick={() => update({ page: filters.page + 1 })}
                  >
                    {t("next")}
                  </button>
                </nav>
              )}
          </div>
        )
      )}
    </section>
  );
}
